import {
	Add,
	CastForEducation,
	Delete,
	KeyboardArrowDown,
	KeyboardArrowUp,
	MenuBook,
	MessageOutlined,
	MoreVert,
	SwapHoriz,
	Warning,
} from "@mui/icons-material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import EditIcon from "@mui/icons-material/Edit";
import NightlightIcon from "@mui/icons-material/Nightlight";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import {
	Box,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { alpha, lighten } from "@mui/material/styles";
import { styled } from "@mui/system";
import { useQueryClient } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import { type ComponentProps, type FC, Fragment, type MouseEvent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { ThemedSvg } from "../../components/UIToolkit/ThemedSvg";
import { AnalyticValuesProvider } from "../../contexts/analytics/AnalyticValuesProvider";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useDraftMode } from "../../contexts/DraftModeContext";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { copyDayData, useHospitalData } from "../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../contexts/RequestsContext";
import { useSelfScheduling } from "../../contexts/SelfScheduling/SelfSchedulingContext";
import { useUserSettingsContext } from "../../contexts/Staffing/UserSettingsContext";
import { isDefaultShiftTime } from "../../contexts/utils/shift-times";
import { useWeekViewContext } from "../../contexts/WeekView";
import { useExpandedGroups } from "../../hooks/useExpandedGroups";
import { useHandleNurseChange } from "../../hooks/useHandleNurseChange";
import { computeHppdBreakdown } from "../../hooks/useHppdByDimension";
import { useFormatPersonName } from "../../hooks/useNameFormat";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { usePublishChanges } from "../../hooks/usePublishChanges";
import { useRevealWage } from "../../hooks/useRevealWage";
import { useShiftWageBreakdown } from "../../hooks/useShiftWageBreakdown";
import { useValueEveryMinute } from "../../hooks/useValueEveryMinute";
import { invalidatePbjPredictionJobCoverage } from "../../pages/PbjModel/hooks/pbjQueryOptions";
import { backendService } from "../../services/backend.service";
import { SHIFT_NIGHT_FALLBACK } from "../../theme/tokens";
import { trackEvent } from "../../utils/analytics";
import { getShiftDisplayDate, isInPTO } from "../../utils/dateUtils";
import { formatLocationLabel } from "../../utils/locationDisplay";
import {
	type BaseNurseShift,
	ChangesSource,
	ChangeType,
	type GroupedNurses,
	HppdSeparationType,
	LocationMode,
	NotificationType,
	type Nurse,
	type NurseFullRole,
	type NurseInShift,
	NurseTrainingStatus,
	type OpenShiftRequest,
	type ReplaceShiftRequest,
	RequestStatus,
	type ShiftChange,
	type ShiftRequest,
	StatsMode,
} from "../../utils/models";
import { applyShiftJobLocal } from "../../utils/shiftJobUpdate";
import { buildNurseUnionMap, getShiftIndexByShiftTimes, inverseRolesMap, shiftTypeToIndex } from "../../utils/utilFunctions";
import { canOpenShiftWageBreakdown, isWageBreakdownAvailable } from "../../utils/wageUtils";
import { NormalizedRequestApprovalDialog } from "../approvalDialogs/RequestsApproval/NormalizedRequestApprovalDialog";
import { NurseInShiftNotesInput } from "../DayViewGaps/DailyRow/NurseInShiftTrailingNode/NurseInShiftNotesInput";
import { EditJobPopover } from "../EditJobPopover/EditJobPopover";
import { EditTimePopover } from "../EditTimePopover";
import { usePayrollConfirmation } from "../hooks/usePayrollConfirmation";
import { InHouseTooltip } from "../InHouseTooltip/InHouseTooltip";
import { LocationPicker } from "../LocationPicker/LocationPicker";
import { DeleteRecurringOrSingleShift } from "../NormalizedNurseMenu/DeleteRecurringOrSingleShift/DeleteRecurringOrSingleShift";
import { useRemovalOffersReplacementSearch } from "../NormalizedNurseMenu/DeleteRecurringOrSingleShift/useRemovalOffersReplacementSearch";
import { NormalizedNurseMenu } from "../NormalizedNurseMenu/NormalizedNurseMenu";
import { NurseShiftBadges } from "../NurseShiftBadges/NurseShiftBadges";
import { NurseUnionIndicator } from "../NurseUnionIndicator/NurseUnionIndicator";
import { DirectMessage } from "../StaffCommunication/util/Direct/DirectMessage";
import ColorfulRole from "../UIToolkit/ColorfulRole/ColorfulRole";
import SystemButton from "../UIToolkit/SystemButton/SystemButton";
import { type ReopenCandidate, useReopenWageBreakdownRow } from "../WageBreakdownReturn/useReopenWageBreakdownRow";
import { WageBreakdownSubTable } from "../WageBreakdownSubTable";
import { buildBadgeRequestFinder } from "./activeRequestBadge";
import { buildJobRatios, buildRoleRatios, CensusGroupHeaderContent } from "./CensusGroupHeaderContent";
import { tableCallOffAnchorShiftId } from "./callOffAnchor";
import { TraineeIcon } from "./icons";
import { ShiftRemoveButton } from "./ShiftRemoveButton";
import { useCensusGrouping } from "./useCensusGrouping";
import { formatTotalHours, getAutoGroupSubtext, getShiftTotalHours, getStaffTableColumnWidths } from "./utils";

const WARNING_HOURS_DIFF = 17;
const editIconClassName = "edit-icon";
type NursesTableBodyProps = {
	staff: NurseInShift[];
	showActual: boolean;
	weekIndex: number;
	dayIndex: number;
	date: DateTime;
	recentCallInsCounts?: Record<number, number>;
	selectedTimeRange?: { start: DateTime; end: DateTime };
	selectedRoles?: NurseFullRole[] | "All";
	/** A staff member has been picked in the day's staff search, so their group opens itself. */
	isStaffChosen: boolean;
};

type Props = ComponentProps<typeof TableRow> & {
	nurseShift: NurseInShift;
	showActual: boolean;
	isWarningRow: (_: NurseInShift) => boolean;
};

export const StyledTableRow: FC<Props> = ({ nurseShift, showActual, isWarningRow, sx, children, ...rest }) => {
	const isWarning = isWarningRow(nurseShift);

	const backgroundColor = isWarning
		? "#F8D9D6"
		: nurseShift.isPendingToDelete
			? "#FDE5E5"
			: nurseShift.isPending
				? "#1DD87133"
				: nurseShift.isPendingToUpdate
					? "#CEF8FF"
					: "white";

	const isFaded =
		nurseShift.isPendingToDelete || (showActual && "startTime" in nurseShift && "endTime" in nurseShift && nurseShift.startTime === nurseShift.endTime);

	return (
		<TableRow
			{...rest}
			sx={{
				position: "relative",
				cursor: "pointer",
				height: "36px",

				"& td": {
					fontSize: "0.875rem",
					color: "primary.dark",
					border: "none",
					padding: "0 1.125rem",
					backgroundColor,
					borderBottom: "2px solid #f9f7ff",
					textAlign: "center",
					verticalAlign: "middle",
					opacity: isFaded ? 0.5 : 1,
				},

				"&:hover td": {
					backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
				},

				...sx,
			}}
		>
			{children}
		</TableRow>
	);
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
	[`.${editIconClassName}`]: {
		opacity: 0,
		transition: "opacity 0.2s",
	},
	[`&:hover .${editIconClassName}`]: {
		opacity: 1,
	},
}));

export const NursesTableBody: FC<NursesTableBodyProps> = ({
	staff,
	showActual,
	dayIndex,
	weekIndex,
	date,
	recentCallInsCounts,
	selectedTimeRange,
	selectedRoles,
	isStaffChosen,
}) => {
	const { t } = useTranslation();
	const { isDraftMode } = useDraftMode();
	const { isDataInSelfSchedulingPeriod } = useSelfScheduling();
	const {
		hospitalData,
		setHospitalData,
		updateNurseLocation,
		getShiftDate,
		restoreNurseToSpecificDate,
		removeNurseCompletelyFromShift,
		chosenDepartment,
		timezone,
		showErrorSnackbar,
		getNurseById,
		getNurseFormattedName,
		isHospital,
		isHospitalDemo,
		isHospitalView,
	} = useHospitalData();
	const showHospitalView = isHospitalView || isHospitalDemo;
	const { weekViewData } = useWeekViewContext();
	const { settings } = useUserSettingsContext();
	const { track, trackError } = useAnalytics();
	const queryClient = useQueryClient();
	const columnWidths = getStaffTableColumnWidths(showHospitalView);
	const unionByNurseId = useMemo(() => buildNurseUnionMap(hospitalData.nurses), [hospitalData.nurses]);
	// Hospital care metric (WLS): the day's average patient workload score across its shifts (Day + Night).
	// Shown in the location group header where SNF/ALF show HPPD. Backend-computed `score`; we only average.
	const wls = useMemo<number | null>(() => {
		if (!isHospital) return null;
		const scores = (hospitalData.weeks[weekIndex]?.weeklyShifts ?? [])
			.map((shift) => shift.days[dayIndex]?.score)
			.filter((score): score is number => typeof score === "number");
		return scores.length ? scores.reduce((sum, score) => sum + score, 0) / scores.length : null;
	}, [isHospital, hospitalData.weeks, weekIndex, dayIndex]);

	const navigate = useNavigate();
	const location = useLocation();

	const onDoubleClickNurse = useCallback(
		(nurse: NurseInShift) => {
			navigate(`/${chosenDepartment.department_id}/nurse/${nurse.nurseId}`, {
				state: { navigationSrc: location.pathname },
			});
		},
		[navigate, chosenDepartment.department_id, location.pathname],
	);

	const requests = useMemo(
		() =>
			weekViewData[date.weekday % 7].requests.filter(
				(request): request is ReplaceShiftRequest | OpenShiftRequest =>
					request.request_type === NotificationType.ReplaceShift || request.request_type === NotificationType.ReplaceAgency,
			),
		[weekViewData, date],
	);
	const { discardNurseRemove } = useHandleNurseChange();
	const { mutateAsync: publishChanges } = usePublishChanges();
	const canViewWage = useRevealWage();
	const { myRequests } = useRequestsContext();
	const { groupNursesByLocation } = useCensusGrouping(date);
	const formatPersonName = useFormatPersonName();

	const getNurseFullName = useCallback(
		(nurseId: number): string => {
			const nurse = getNurseById(nurseId);
			if (!nurse) return getNurseFormattedName(nurseId);
			return formatPersonName(nurse);
		},
		[getNurseById, getNurseFormattedName, formatPersonName],
	);

	const [actionsMenuAnchorEl, setActionsMenuAnchorEl] = useState<null | HTMLElement>(null);
	const [actionNurse, setActionNurse] = useState<NurseInShift | null>(null);
	const [directMessageNurseId, setDirectMessageNurseId] = useState<number>();
	const [requestApprovalDialog, setRequestApprovalDialog] = useState<{ open: boolean; request: ShiftRequest | null; nurseShift: NurseInShift | null }>({
		open: false,
		request: null,
		nurseShift: null,
	});

	const [editingNotes, setEditingNotes] = useState<{ id: string; element: HTMLElement | null } | null>(null);
	const [editingLocation, setEditingLocation] = useState<{ id: string; element: HTMLElement | null } | null>(null);
	const [editingJob, setEditingJob] = useState<{ id: string; element: HTMLElement | null } | null>(null);
	const [editingTime, setEditingTime] = useState<{ id: string; element: HTMLElement | null } | null>(null);
	const [expandedRow, setExpandedRow] = useState<string | null>(null);
	const {
		data: expandedBreakdown,
		isLoading: isBreakdownLoading,
		isError: isBreakdownError,
		refetch: refetchBreakdown,
	} = useShiftWageBreakdown(expandedRow, chosenDepartment.department_id, showActual, canViewWage);
	const [deleteNurse, setDeleteNurse] = useState<NurseInShift | null>(null);
	const [confirmationDialog, setConfirmationDialog] = useState<{ open: boolean; title: string; message: string; onConfirm: () => void }>({
		open: false,
		title: "",
		message: "",
		onConfirm: () => {},
	});
	const needsPayrollConfirmation = usePayrollConfirmation(showActual);

	const publishEditChange = useCallback(
		async (nurse: NurseInShift, onFailure: (error: unknown) => void, actual: boolean, editType: string) => {
			const nurseToPublish = { ...nurse, isPendingToUpdate: isDraftMode ? true : nurse.isPendingToUpdate };

			if (isDraftMode) {
				// Update hospital data optimistically (auto-tracking will detect the change)
				setHospitalData((prevData) => {
					const shiftIndex = prevData.weeks[weekIndex].weeklyShifts.findIndex((s) => s.type === nurseToPublish.shiftType);
					const { day, newData } = copyDayData(prevData, weekIndex, shiftIndex, dayIndex);

					if (actual) {
						day.nursesActual = day.nursesActual?.map((n) => (n.id !== nurseToPublish.id ? n : { ...nurseToPublish }));
					} else {
						day.nurses = day.nurses.map((n) => (n.id !== nurseToPublish.id ? n : { ...nurseToPublish }));
					}
					return newData;
				});
				return;
			}

			let actualChanges: ShiftChange[] = [];
			let changes: ShiftChange[] = [];

			const shiftIndex = shiftTypeToIndex(nurseToPublish.shiftType);

			const changesToPublish: ShiftChange[] = [
				{
					nurse: nurseToPublish,
					shiftIndex,
					shiftType: nurseToPublish.shiftType,
					weekIndex,
					dayIndex,
					date: nurse.startTime.set({ hour: 0, minute: 0, second: 0, millisecond: 0 }),
					departmentId: chosenDepartment.department_id,
					changeType: ChangeType.Update,
				},
			];

			if (actual) actualChanges = changesToPublish;
			else changes = changesToPublish;

			try {
				trackEvent(`day view ${editType} edited`, { source: ChangesSource.DaySchedule, changes: changes, actual_changes: actualChanges });

				if (!isDraftMode) {
					await publishChanges({
						accepted_requests: {},
						declined_requests: {},
						changes: changes,
						actual_changes: actualChanges,
						source: ChangesSource.DaySchedule,
					});
				}

				setHospitalData((prevData) => {
					const shiftIndex = prevData.weeks[weekIndex].weeklyShifts.findIndex((s) => s.type === nurseToPublish.shiftType);
					const { day, newData } = copyDayData(prevData, weekIndex, shiftIndex, dayIndex);

					if (actual) {
						day.nursesActual = day.nursesActual?.map((nurse) => (nurse.id !== nurseToPublish.id ? nurse : { ...nurseToPublish }));
					} else {
						day.nurses = day.nurses.map((nurse) => (nurse.id !== nurseToPublish.id ? nurse : { ...nurseToPublish }));
					}
					return newData;
				});
			} catch (error) {
				console.error("Error details:", error);
				onFailure(error);
				showErrorSnackbar("Failed to update nurse", { action: `day view ${editType} edit failed`, source: ChangesSource.DaySchedule }, error);
			}
		},
		[chosenDepartment.department_id, setHospitalData, weekIndex, dayIndex, showErrorSnackbar, isDraftMode, publishChanges],
	);

	const handleLocationChange = useCallback(
		async (id: string, newLocation: string, actual: boolean): Promise<void> => {
			const nurse: BaseNurseShift | undefined = staff.find((n) => n.id === id);

			if (!nurse) return;

			const oldLocation = nurse.location || "";

			setEditingLocation(null);

			await publishEditChange(
				{
					...nurse,
					location: newLocation,
					previousLocation: isDraftMode && !nurse.previousLocation ? oldLocation : nurse.previousLocation,
				},
				(error: unknown) => {
					updateNurseLocation(id, weekIndex, shiftTypeToIndex(nurse.shiftType), dayIndex, oldLocation, actual);
					console.error("Error publishing location change:", error);
				},
				actual,
				"location",
			);
		},
		[weekIndex, dayIndex, updateNurseLocation, staff, publishEditChange, isDraftMode],
	);

	const applyScheduledShiftJobLocal = useCallback(
		(shiftId: string, nurseId: number, newJobId: string, excludeFromPpd: boolean) => {
			setHospitalData((prev) => applyShiftJobLocal(prev, { shiftId, nurseId, newJobId, excludeFromPpd, isActual: showActual }));
		},
		[showActual, setHospitalData],
	);

	const [ptoWarningState, setPtoWarningState] = useState<{ open: boolean; onConfirm: () => void }>({ open: false, onConfirm: () => {} });

	const handleTimeChange = useCallback(
		async (id: string, newStartTime: DateTime, newEndTime: DateTime, showActual: boolean) => {
			const nurse: BaseNurseShift | undefined = staff.find((n) => n.id === id);

			if (!nurse) return;

			const fullNurse = getNurseById(nurse.nurseId);
			const hasPtoOverlap = fullNurse?.employeeSpecificPreferences.some((pref) => isInPTO(pref, newStartTime, chosenDepartment.dayStart, newEndTime));

			// Store old scheduled times for rollback
			const oldStartTime = nurse.startTime;
			const oldEndTime = nurse.endTime;

			setEditingTime(null);

			const executeTimeChange = async () => {
				await publishEditChange(
					{
						...nurse,
						startTime: newStartTime,
						endTime: newEndTime,
						previousStartTime: isDraftMode && !nurse.previousStartTime ? oldStartTime : nurse.previousStartTime,
						previousEndTime: isDraftMode && !nurse.previousEndTime ? oldEndTime : nurse.previousEndTime,
					},
					(error: unknown) => {
						if (nurse !== undefined) {
							nurse.startTime = oldStartTime;
							nurse.endTime = oldEndTime;
						}
						console.error("Error publishing time change:", error);
					},
					showActual,
					"time",
				);

				setHospitalData((prevData) => {
					const newData = { ...prevData };
					const nurseData = newData.nurses.find((n) => n.id === nurse.nurseId);
					if (nurseData) {
						nurseData.baseWages = nurseData.baseWages.map((bw) => {
							if (
								bw.startTime.startOf("minute") === oldStartTime.startOf("minute") &&
								bw.endTime &&
								bw.endTime.startOf("minute") === oldEndTime.startOf("minute")
							) {
								return { ...bw, startTime: newStartTime, endTime: newEndTime };
							}
							return bw;
						});
					}
					return newData;
				});

				track("Time Change", { id, nurse_id: nurse.id, new_start_time: newStartTime.toFormat("HH:mm"), new_end_time: newEndTime.toFormat("HH:mm") });
			};

			if (hasPtoOverlap) {
				setPtoWarningState({
					open: true,
					onConfirm: async () => {
						setPtoWarningState({ open: false, onConfirm: () => {} });
						await executeTimeChange();
					},
				});
			} else if (needsPayrollConfirmation && showActual) {
				setConfirmationDialog({
					open: true,
					title: "Confirm Time Change",
					message: "This will update actual working hours data. Are you sure you want to change the time?",
					onConfirm: async () => {
						setConfirmationDialog({ open: false, title: "", message: "", onConfirm: () => {} });
						await executeTimeChange();
					},
				});
			} else {
				await executeTimeChange();
			}
		},
		[publishEditChange, staff, needsPayrollConfirmation, setHospitalData, track, isDraftMode, getNurseById, chosenDepartment.dayStart],
	);

	const canSendDirectMessage = useCallback(
		(nurseShift: NurseInShift): boolean => {
			const nurse: Nurse | undefined = getNurseById(nurseShift.nurseId);
			if (!nurse) return false;

			return !!nurse.userId && !!nurse.phoneNumber && nurseShift.endTime === nurseShift.startTime;
		},
		[getNurseById],
	);

	const handleCancelClick = async (): Promise<void> => {
		if (!actionNurse) return;

		if (actionNurse.isPendingToDelete) {
			// Keep the nurse and remove the pending status
			await discardNurseRemove({
				nurse: actionNurse,
				shiftIndex: shiftTypeToIndex(actionNurse.shiftType),
				weekIndex,
				dayNo: dayIndex,
				shiftType: actionNurse.shiftType,
			});
			restoreNurseToSpecificDate(
				weekIndex,
				dayIndex,
				shiftTypeToIndex(actionNurse.shiftType),
				actionNurse.nurseId,
				showActual,
				isDataInSelfSchedulingPeriod(actionNurse.startTime),
			);
		} else {
			// Remove the nurse completely if it was a pending addition
			removeNurseCompletelyFromShift(weekIndex, dayIndex, shiftTypeToIndex(actionNurse.shiftType), actionNurse.nurseId, showActual);
		}
	};

	const handleRowClick = useCallback((nurse: NurseInShift) => {
		setExpandedRow((row) => (row === nurse.id ? null : nurse.id));
	}, []);

	const handleActionsMenuOpen = useCallback((event: MouseEvent<HTMLElement>, nurse: NurseInShift) => {
		event.stopPropagation();
		setActionsMenuAnchorEl(event.currentTarget);
		setActionNurse(nurse);
	}, []);

	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	// The same predicate the removal dialog renders its replacement checkbox from, so the call-off tour's
	// spotlit trash icon can never open a dialog without it.
	const removalOffersReplacementSearch = useRemovalOffersReplacementSearch(showActual);

	const isWarningRow = useCallback(
		(shift: NurseInShift): boolean => {
			try {
				const nurse = getNurseById(shift.nurseId);
				if (!nurse) return false;

				if (!isWageBreakdownAvailable(nurse, shift.startTime, { jobManagementEnabled, shiftExcludeFromPpd: shift.excludeFromPpd })) return false;

				const shiftLength: number = shift.endTime.diff(shift.startTime, "hours").hours;
				return shiftLength === 0 || shiftLength >= WARNING_HOURS_DIFF;
			} catch {
				return false;
			}
		},
		[getNurseById, jobManagementEnabled],
	);

	// Generate consistent random number based on shift ID
	const getPatientCount = (shiftId: string): number => {
		const hash = shiftId.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
		return 5 + (hash % 4); // Random between 5-8
	};

	const getTotalWorkload = (patientCount: number, shiftId: string): string => {
		const hash = shiftId.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
		const multiplier = 0.8 + (hash % 41) / 100; // Random between 0.8-1.2
		return (patientCount * multiplier).toFixed(2);
	};

	const nursesWithReplaceRequests = useMemo(() => {
		const requestMap = new Map<number, ShiftRequest>();
		if (showActual || !requests.length) return requestMap;

		requests.forEach((request) => {
			if ("replacement_nurse_id" in request && request.replacement_nurse_id) {
				requestMap.set(request.replacement_nurse_id, request);
			}
		});
		return requestMap;
	}, [requests, showActual]);

	// The Active Request badge follows the shift the request is for, not the nurse.
	const findBadgeRequest = useMemo(() => buildBadgeRequestFinder(showActual ? [] : requests), [requests, showActual]);

	const sortedStaff = useMemo(() => {
		if (showActual || !nursesWithReplaceRequests.size) return staff;

		const withRequests: NurseInShift[] = [];
		const withoutRequests: NurseInShift[] = [];

		staff.forEach((nurseShift) => {
			if (nursesWithReplaceRequests.has(nurseShift.nurseId)) {
				withRequests.push(nurseShift);
			} else {
				withoutRequests.push(nurseShift);
			}
		});

		return [...withRequests, ...withoutRequests];
	}, [staff, nursesWithReplaceRequests, showActual]);

	const locationFilters = useMemo(() => {
		const hasRoleFilter = selectedRoles && selectedRoles !== "All";
		const hasTimeFilter = !!selectedTimeRange;
		if (!hasRoleFilter && !hasTimeFilter) return undefined;
		return { roles: selectedRoles, timeRange: selectedTimeRange };
	}, [selectedRoles, selectedTimeRange]);

	const groupedNurses = useMemo<GroupedNurses[]>(
		() => groupNursesByLocation(sortedStaff, locationFilters),
		[sortedStaff, groupNursesByLocation, locationFilters],
	);

	// Picking someone in the staff search narrows `sortedStaff` to just them, so the group that still
	// holds a nurse is the group they are in — open it. `groupNursesByLocation` keeps zero-nurse groups
	// alive when their census is non-zero, so the nurse COUNT is the signal, not the group's existence.
	// Empty while nobody is picked, which is what returns every group to however the user left it.
	const autoExpandedGroups = useMemo<ReadonlySet<string>>(
		() => (isStaffChosen ? new Set(groupedNurses.filter((group) => group.nurses.length > 0).map((group) => group.locationKey)) : new Set()),
		[isStaffChosen, groupedNurses],
	);
	const { expandedGroups, setExpandedGroups, toggleGroupExpand } = useExpandedGroups({ autoExpandedGroups });

	// DAT-906 · Returned here by "Back to wage breakdown": open that shift's group and breakdown again.
	const reopenCandidates = useMemo<ReopenCandidate[]>(
		() =>
			groupedNurses.flatMap((group) =>
				group.nurses
					.filter((shift) => canOpenShiftWageBreakdown(shift, getNurseById(shift.nurseId), jobManagementEnabled))
					.map((shift) => ({ shiftId: shift.id, groupKey: group.locationKey })),
			),
		[groupedNurses, getNurseById, jobManagementEnabled],
	);
	const openGroup = useCallback(
		(groupKey: string) => setExpandedGroups((prev) => (prev.has(groupKey) ? prev : new Set(prev).add(groupKey))),
		[setExpandedGroups],
	);
	const reopened = useReopenWageBreakdownRow({
		candidates: reopenCandidates,
		isActual: showActual,
		openGroup,
		openRow: setExpandedRow,
		expandedRow,
	});

	// The ONE trash icon the call-off walkthrough spotlights. Group rendering mirrors `isCollapsed` below. Re-read every
	// minute: a shift drifts out of (or into) the call-off window with no data change.
	const pickCallOffAnchorShiftId = useCallback(
		(now: DateTime) =>
			tableCallOffAnchorShiftId({
				groups: groupedNurses,
				isGroupRendered: (locationKey) => settings.censusByLocation === LocationMode.None || expandedGroups.has(locationKey),
				offersReplacementSearch: removalOffersReplacementSearch,
				now,
			}),
		[groupedNurses, settings.censusByLocation, expandedGroups, removalOffersReplacementSearch],
	);
	const callOffAnchorShiftId = useValueEveryMinute(pickCallOffAnchorShiftId);

	const handleReplaceRequestRowClick = useCallback(
		(nurseShift: NurseInShift) => {
			if (showActual) return;

			const request = nursesWithReplaceRequests.get(nurseShift.nurseId);
			if (request) setRequestApprovalDialog({ open: true, request, nurseShift });
		},
		[nursesWithReplaceRequests, showActual],
	);

	return (
		<>
			{!!directMessageNurseId && (
				<DirectMessage
					open={!!directMessageNurseId}
					onClose={() => setDirectMessageNurseId(undefined)}
					nurseId={directMessageNurseId}
					initialMessage={`Hi, You are missing punches for the ${date.toFormat("MMMM Do")} shift at ${chosenDepartment.display_name}. Please reach out to HR or admin team to fix your timesheet`}
				/>
			)}
			{groupedNurses.map((group) => {
				const isCollapsed = settings.censusByLocation !== LocationMode.None && !expandedGroups.has(group.locationKey);
				const showGroupHeader = settings.censusByLocation !== LocationMode.None;

				// group.locationKey is the location name in Auto mode. Pass every location
				// sharing that name so the subtext aggregates rooms/unit/floor across variants.
				const autoSubtext =
					settings.censusByLocation === LocationMode.Auto
						? getAutoGroupSubtext(chosenDepartment.locations.filter((l) => l.location === group.locationKey))
						: undefined;

				return (
					<Fragment key={`group-header-${group.locationKey}`}>
						{showGroupHeader && (
							<TableRow
								sx={(theme) => ({
									backgroundColor: theme.palette.primary.dark,
									"&:hover": {
										backgroundColor: lighten(theme.palette.primary.dark, 0.12),
										"& td": { backgroundColor: lighten(theme.palette.primary.dark, 0.12) },
									},
								})}
							>
								<TableCell colSpan={100} sx={{ px: 1.25, py: 0.5, fontSize: "14px", color: "white", bgcolor: "primary.dark", border: "none" }}>
									<CensusGroupHeaderContent
										toggleGroupExpand={() => toggleGroupExpand(group.locationKey)}
										name={group.locationName}
										subtext={autoSubtext}
										locationKey={group.locationKey}
										isCollapsed={isCollapsed}
										date={date}
										weekIndex={weekIndex}
										dayIndex={dayIndex}
										showActual={showActual}
										selectedTimeRange={selectedTimeRange}
										stats={[
											{ value: group.nurses.length, label: "staff" },
											{ value: group.censusCount, label: "census" },
											...(chosenDepartment.statsMode === StatsMode.PPD_HPPD && settings.hppdSeparationType === HppdSeparationType.None
												? [{ label: `HPPD: ${group.hppd.toFixed(2)}` }]
												: []),
											// Hospitals show WLS (workload score) where SNF/ALF show HPPD.
											...(isHospital && wls !== null ? [{ label: `WLS: ${wls.toFixed(1)}` }] : []),
										]}
										hppdBreakdown={
											chosenDepartment.statsMode === StatsMode.PPD_HPPD && settings.hppdSeparationType !== HppdSeparationType.None
												? computeHppdBreakdown(
														group.nurses,
														group.censusCount,
														chosenDepartment.department_id,
														chosenDepartment.breakRulesConfig || [],
														settings.hppdSeparationType,
														chosenDepartment,
														// Positional gap: the two `undefined`s hold the existing defaults; only the union map is new.
														undefined,
														undefined,
														unionByNurseId,
													)
												: undefined
										}
										totalHppd={
											chosenDepartment.statsMode === StatsMode.PPD_HPPD && settings.hppdSeparationType !== HppdSeparationType.None ? group.hppd : undefined
										}
										roleRatios={chosenDepartment.statsMode === StatsMode.Ratio && !jobManagementEnabled ? buildRoleRatios(group) : undefined}
										jobRatios={
											chosenDepartment.statsMode === StatsMode.Ratio && jobManagementEnabled ? buildJobRatios(group, chosenDepartment.jobs) : undefined
										}
									/>
								</TableCell>
							</TableRow>
						)}
						<TableRow>
							<TableCell style={{ padding: 0, border: 0 }} colSpan={100}>
								<Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
									<Table sx={{ tableLayout: "fixed", width: "100%" }}>
										<TableBody>
											{group.nurses.map((nurseShift) => {
												const hasReplaceRequest = nursesWithReplaceRequests.has(nurseShift.nurseId);
												const badgeReplaceRequest = findBadgeRequest(nurseShift);
												const recentCallInsCount = recentCallInsCounts?.[nurseShift.nurseId] ?? 0;
												const nurseDetails = getNurseById(nurseShift.nurseId);
												// Paid hours, unpaid breaks out: the shift column shows them and the wage breakdown's header repeats them.
												const shiftTotalHours = getShiftTotalHours(
													nurseShift.startTime,
													nurseShift.endTime,
													nurseShift.role,
													nurseShift.type,
													chosenDepartment,
													nurseShift.jobId ? [nurseShift.jobId] : [],
													jobManagementEnabled,
													unionByNurseId.get(nurseShift.nurseId) ?? "",
												);

												return (
													<AnalyticValuesProvider key={`nurse-${nurseShift.id}`} props={{ nurseId: nurseShift.nurseId, nurseInShiftId: nurseShift.id }}>
														<StyledTableRow
															data-testid={`day-schedule-nurse-row-${nurseShift.nurseId}`}
															nurseShift={nurseShift}
															showActual={showActual}
															isWarningRow={isWarningRow}
															onClick={(e) => {
																e.stopPropagation();
																if (hasReplaceRequest) {
																	handleReplaceRequestRowClick(nurseShift);
																}
															}}
															onDoubleClick={() => onDoubleClickNurse(nurseShift)}
														>
															{/* Role */}
															<StyledTableCell sx={{ width: columnWidths.role.width, minWidth: columnWidths.role.minWidth }}>
																<Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-start" }}>
																	{nurseShift.isPendingToDelete && <Delete sx={{ fontSize: "18px" }} />}
																	{nurseShift.isPending && !nurseShift.isPendingToDelete && <Add sx={{ fontSize: "18px" }} />}
																	{nurseShift.isPendingToUpdate && !nurseShift.isPendingToDelete && !nurseShift.isPending && (
																		<EditIcon sx={{ fontSize: "18px" }} />
																	)}
																	<ColorfulRole role={nurseShift.role} />
																</Box>
															</StyledTableCell>
															{/* Job */}
															<StyledTableCell sx={{ width: columnWidths.job.width, minWidth: columnWidths.job.minWidth, verticalAlign: "middle", pl: 2 }}>
																<Box
																	sx={{
																		display: "flex",
																		alignItems: "center",
																		gap: 0.5,
																	}}
																>
																	<Tooltip title={nurseShift.jobName}>
																		<Typography
																			sx={{
																				fontSize: "13px",
																				color: "primary.dark",
																				lineHeight: 1.35,
																				textAlign: "center",
																				minWidth: 0,
																				maxWidth: "100%",
																				overflow: "hidden",
																				textOverflow: "ellipsis",
																				whiteSpace: "nowrap",
																			}}
																		>
																			{nurseShift.jobName || "--"}
																		</Typography>
																	</Tooltip>
																	<IconButton
																		className={editIconClassName}
																		size="small"
																		onClick={(e) => {
																			e.stopPropagation();
																			setEditingJob({ id: nurseShift.id, element: e.currentTarget });
																		}}
																		sx={{
																			opacity: 0,
																			transition: "opacity 0.2s",
																			p: "2px",
																			color: "primary.dark",
																			flexShrink: 0,
																			alignSelf: "center",
																		}}
																	>
																		<EditIcon sx={{ fontSize: "1rem" }} />
																	</IconButton>
																</Box>
																{editingJob?.id === nurseShift.id &&
																	(() => {
																		const nurse = getNurseById(nurseShift.nurseId);
																		const nurseJobs = nurse?.jobs ?? [];
																		return (
																			<EditJobPopover
																				currentJobId={nurseShift.jobId ?? ""}
																				availableJobs={nurseJobs}
																				onJobChange={(newJobId) => {
																					if (nurseShift.id) {
																						void backendService
																							.updateShiftJob(nurseShift.id, newJobId, showActual)
																							.then((response) => {
																								track("shift_job_update_success", {
																									shift_id: nurseShift.id,
																									nurse_id: nurseShift.nurseId,
																								});
																								if (showActual) {
																									invalidatePbjPredictionJobCoverage(queryClient);
																								}
																								applyScheduledShiftJobLocal(nurseShift.id, nurseShift.nurseId, newJobId, response.data.excludeFromPpd);
																							})
																							.catch((error: unknown) => {
																								trackError("shift_job_update_error", {
																									shift_id: nurseShift.id,
																									nurse_id: nurseShift.nurseId,
																									error_message: error instanceof Error ? error.message : String(error),
																								});
																								showErrorSnackbar(
																									"Failed to update shift job",
																									{ action: "shift job update failed", source: ChangesSource.DaySchedule },
																									error,
																								);
																							});
																					}
																					setEditingJob(null);
																				}}
																				onClose={() => setEditingJob(null)}
																				anchorEl={editingJob?.element || null}
																			/>
																		);
																	})()}
															</StyledTableCell>
															{/* Name */}
															<StyledTableCell
																sx={{
																	width: columnWidths.name.width,
																	minWidth: columnWidths.name.minWidth,
																	textAlign: "left",
																	pl: 2,
																}}
															>
																<Box
																	sx={{
																		display: "flex",
																		alignItems: "center",
																		justifyContent: "space-between",
																	}}
																>
																	<InHouseTooltip title={nurseShift.name}>
																		<Box
																			sx={{
																				display: "flex",
																				alignItems: "center",
																				gap: 1,
																				whiteSpace: "nowrap",
																				maxWidth: "80%",
																				overflow: "hidden",
																			}}
																		>
																			{nurseShift.replacedWith ? (
																				<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
																					<Typography sx={{ fontSize: "14px", textDecoration: nurseShift.isPendingToDelete ? "line-through" : "none" }}>
																						{getNurseFullName(nurseShift.nurseId)}
																					</Typography>
																					<SwapHoriz sx={{ fontSize: "16px", color: (theme) => alpha(theme.palette.primary.dark, 0.5) }} />
																					<Typography sx={{ fontSize: "14px" }}>{getNurseFullName(nurseShift.replacedWith)}</Typography>
																				</Box>
																			) : (
																				<Typography
																					sx={{
																						fontSize: "14px",
																						textDecoration: nurseShift.isPendingToDelete ? "line-through" : "none",
																						overflow: "hidden",
																						textOverflow: "ellipsis",
																						whiteSpace: "nowrap",
																					}}
																				>
																					{getNurseFullName(nurseShift.nurseId)}
																				</Typography>
																			)}

																			{nurseShift.charge && (
																				<Box
																					component="span"
																					sx={{
																						width: 16,
																						height: 16,
																						borderRadius: "50%",
																						display: "flex",
																						alignItems: "center",
																						justifyContent: "center",
																					}}
																				>
																					<ThemedSvg src="/charge.svg" alt="charge" color="var(--inhouse-primary)" />
																				</Box>
																			)}
																			{nurseShift.trainingStatus === NurseTrainingStatus.Trainee && (
																				<Box
																					component="span"
																					sx={{
																						width: 16,
																						height: 16,
																						borderRadius: "50%",
																						display: "flex",
																						alignItems: "center",
																						justifyContent: "center",
																					}}
																				>
																					<TraineeIcon />
																				</Box>
																			)}
																			{nurseShift.trainingStatus === NurseTrainingStatus.Orientation && (
																				<Box
																					component="span"
																					sx={{
																						width: 15,
																						height: 15,
																						borderRadius: "50%",
																						display: "flex",
																						alignItems: "center",
																						justifyContent: "center",
																					}}
																				>
																					<MenuBook sx={{ width: "18px", height: "18px" }} />
																				</Box>
																			)}
																			{nurseShift.trainingStatus === NurseTrainingStatus.Education && (
																				<Box
																					component="span"
																					sx={{
																						width: 15,
																						height: 15,
																						borderRadius: "50%",
																						display: "flex",
																						alignItems: "center",
																						justifyContent: "center",
																					}}
																				>
																					<CastForEducation sx={{ width: "18px", height: "18px" }} />
																				</Box>
																			)}
																			<NurseUnionIndicator nurseIdOrNurseObject={nurseDetails} />
																		</Box>
																	</InHouseTooltip>
																	<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
																		{hasReplaceRequest && (
																			<InHouseTooltip title={"Replace Request"}>
																				<SwapHoriz sx={{ color: (theme) => alpha(theme.palette.primary.dark, 0.5), fontSize: "18px" }} />
																			</InHouseTooltip>
																		)}
																		{nurseShift.recurringShiftId && (
																			<InHouseTooltip title={"Recurring Shift"}>
																				<AutorenewIcon sx={{ color: (theme) => alpha(theme.palette.primary.dark, 0.5), fontSize: "18px" }} />
																			</InHouseTooltip>
																		)}
																		{recentCallInsCount > 1 && (
																			<InHouseTooltip
																				title={`${nurseDetails?.firstName ?? nurseShift.name} had ${recentCallInsCount} call-in's in the last 60 days`}
																			>
																				<Warning sx={{ fontSize: "18px" }} />
																			</InHouseTooltip>
																		)}
																	</Box>
																</Box>
															</StyledTableCell>
															{/* Shift Hours */}
															<StyledTableCell
																sx={{ width: columnWidths.shift.width, minWidth: columnWidths.shift.minWidth, textAlign: "left", verticalAlign: "middle" }}
															>
																<Box sx={{ display: "flex", justifyContent: "flex-start" }}>
																	<Box
																		sx={{
																			alignItems: "center",
																			gap: 1,
																			color: "primary.dark",
																			position: "relative",
																			display: "flex",
																			minWidth: "180px",

																			"&:hover .edit-icon": { opacity: 1 },
																		}}
																	>
																		<Typography
																			sx={{
																				fontSize: "14px",
																				opacity: showActual || isDefaultShiftTime(nurseShift, chosenDepartment) ? 0.5 : 1,
																				cursor: "pointer",
																			}}
																			onClick={(e) => {
																				e.stopPropagation();
																				setEditingTime({ id: nurseShift.id, element: e.currentTarget });
																			}}
																		>
																			{nurseShift.isPendingToUpdate && nurseShift.previousStartTime && nurseShift.previousEndTime ? (
																				<Box>
																					<Box sx={{ textDecoration: "line-through", color: "text.secondary" }}>
																						{nurseShift.previousStartTime.setZone(timezone).toFormat("h:mm a")} -{" "}
																						{nurseShift.previousEndTime.setZone(timezone).toFormat("h:mm a")}
																					</Box>
																					<Box>
																						{nurseShift.startTime.setZone(timezone).toFormat("h:mm a")} -{" "}
																						{nurseShift.endTime.setZone(timezone).toFormat("h:mm a")}
																					</Box>
																				</Box>
																			) : (
																				<>
																					{nurseShift.startTime.setZone(timezone).toFormat("h:mm a")} -{" "}
																					{nurseShift.endTime.setZone(timezone).toFormat("h:mm a")}
																				</>
																			)}
																		</Typography>
																		<IconButton
																			className={editIconClassName}
																			size="small"
																			onClick={(e) => {
																				e.stopPropagation();
																				setEditingTime({ id: nurseShift.id, element: e.currentTarget });
																			}}
																			sx={{ opacity: 0, transition: "opacity 0.2s", p: "2px", ml: "4px", color: "primary.dark" }}
																		>
																			<EditIcon sx={{ fontSize: "1rem" }} />
																		</IconButton>
																	</Box>
																	<Box
																		sx={{
																			alignItems: "center",
																			color: "primary.dark",
																			position: "relative",
																			"&:hover .edit-icon": { opacity: 1 },
																			display: "flex",
																		}}
																	>
																		<Box
																			sx={{
																				display: "flex",
																				alignItems: "center",
																				opacity: showActual || isDefaultShiftTime(nurseShift, chosenDepartment) ? 0.5 : 1,
																				cursor: "pointer",
																			}}
																			onClick={(e) => {
																				e.stopPropagation();
																				setEditingTime({ id: nurseShift.id, element: e.currentTarget });
																			}}
																		>
																			<Typography sx={{ minWidth: "56px", textAlign: "right" }}>{formatTotalHours(shiftTotalHours)}</Typography>
																			<Box sx={{ width: "20px", display: "flex", justifyContent: "center" }}>
																				{getShiftIndexByShiftTimes({ startTime: nurseShift.startTime, endTime: nurseShift.endTime }) === 0 ? (
																					<WbSunnyIcon sx={{ color: isHospitalView ? "#000000" : "#FBBB00", fontSize: 16, opacity: 0.7 }} />
																				) : (
																					<NightlightIcon sx={{ color: isHospitalView ? "#000000" : SHIFT_NIGHT_FALLBACK, fontSize: 16, opacity: 0.7 }} />
																				)}
																			</Box>
																		</Box>
																		{(() => {
																			const swapRequest = myRequests.find(
																				(req) =>
																					req.request_type === NotificationType.Swap &&
																					(req as ReplaceShiftRequest).request_for_shift === nurseShift.id &&
																					req.status === RequestStatus.Active,
																			);
																			if (swapRequest?.request_target_shift) {
																				let targetShiftDate: DateTime | null = null;
																				for (const week of hospitalData.weeks) {
																					for (const shift of week.weeklyShifts) {
																						for (const day of shift.days) {
																							const targetShift = day.nurses.find((n) => n.id === swapRequest.request_target_shift);
																							if (targetShift) {
																								targetShiftDate = getShiftDisplayDate(
																									targetShift.startTime,
																									chosenDepartment.dayStart,
																									chosenDepartment.scheduled_shift_delta_minutes,
																								);
																								break;
																							}
																						}
																						if (targetShiftDate) break;
																					}
																					if (targetShiftDate) break;
																				}
																				if (targetShiftDate) {
																					return (
																						<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 0.5 }}>
																							<SwapHorizIcon sx={{ fontSize: 14, color: "primary.main" }} />
																							<Typography sx={{ fontSize: "0.75rem", color: "primary.main" }}>
																								Has active swap request to {targetShiftDate.toFormat("MMM D")}
																							</Typography>
																						</Box>
																					);
																				}
																			}
																			return null;
																		})()}
																	</Box>
																</Box>
																{editingTime?.id === nurseShift.id && (
																	<EditTimePopover
																		role={inverseRolesMap[nurseShift.role]}
																		jobId={nurseShift.jobId}
																		startTime={nurseShift.startTime}
																		endTime={nurseShift.endTime}
																		onTimeChange={({ newStartTime, newEndTime }) => {
																			handleTimeChange(nurseShift.id, newStartTime, newEndTime, showActual);
																		}}
																		onClose={() => setEditingTime(null)}
																		anchorEl={editingTime?.element || null}
																		isActual={showActual}
																	/>
																)}
															</StyledTableCell>
															{/* Location */}
															<StyledTableCell
																sx={{
																	width: columnWidths.location.width,
																	minWidth: columnWidths.location.minWidth,
																	textAlign: "left",
																	verticalAlign: "middle",
																}}
															>
																{editingLocation?.id === nurseShift.id ? (
																	<LocationPicker
																		data-testid={`location-picker-${nurseShift.id}`}
																		locations={chosenDepartment.locations}
																		selectedLocation={nurseShift.location || ""}
																		currentLocation={nurseShift.location || undefined}
																		nurseId={nurseShift.nurseId}
																		nurseFirstName={getNurseById(nurseShift.nurseId)?.firstName}
																		weekIndex={weekIndex}
																		dayNo={dayIndex}
																		shiftIndex={shiftTypeToIndex(nurseShift.shiftType)}
																		isActual={showActual}
																		clearable
																		onClose={() => setEditingLocation(null)}
																		onChange={(location, meta) => {
																			handleLocationChange(nurseShift.id, location, showActual);
																			track("Location Picker Selected", {
																				surface: "day-view",
																				nurse_id: nurseShift.nurseId,
																				location,
																				below_par: meta.belowPar,
																				recent_shifts: meta.recentShifts,
																			});
																		}}
																	/>
																) : (
																	<Box
																		sx={{
																			display: "flex",
																			alignItems: "center",
																			justifyContent: "flex-start",
																			gap: 1,
																			position: "relative",
																			whiteSpace: "nowrap",
																			"&:hover .edit-icon": { opacity: 1 },
																		}}
																		onClick={(e) => {
																			e.stopPropagation();
																			track("Location Change", {
																				nurse_id: nurseShift.nurseId,
																				new_location: nurseShift.location,
																				date: getShiftDate(weekIndex, shiftTypeToIndex(nurseShift.shiftType), dayIndex).toFormat("MM/DD/yyyy"),
																			});
																			setEditingLocation({ id: nurseShift.id, element: e.currentTarget });
																		}}
																	>
																		<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
																			{nurseShift.isPendingToUpdate && nurseShift.previousLocation ? (
																				<Box>
																					<Box sx={{ textDecoration: "line-through", color: "text.secondary" }}>
																						{formatLocationLabel(nurseShift.previousLocation)}
																					</Box>
																					<Box>{formatLocationLabel(nurseShift.location)}</Box>
																				</Box>
																			) : nurseShift.location ? (
																				formatLocationLabel(nurseShift.location)
																			) : (
																				<Typography sx={{ opacity: 0.3 }}>Location</Typography>
																			)}
																		</Box>
																		<IconButton
																			className={editIconClassName}
																			size="small"
																			onClick={(e) => {
																				e.stopPropagation();
																				track("Location Change", {
																					nurse_id: nurseShift.nurseId,
																					new_location: nurseShift.location,
																					date: getShiftDate(weekIndex, shiftTypeToIndex(nurseShift.shiftType), dayIndex).toFormat("MM/DD/yyyy"),
																				});
																				setEditingLocation({ id: nurseShift.id, element: e.currentTarget });
																			}}
																			sx={{ opacity: 0, transition: "opacity 0.2s", p: "2px", ml: "4px", color: "primary.dark" }}
																		>
																			<EditIcon sx={{ fontSize: "1rem" }} />
																		</IconButton>
																	</Box>
																)}
															</StyledTableCell>

															{/* Number of Patients - Hospital Demo Only */}
															{showHospitalView && (
																<StyledTableCell sx={{ width: columnWidths.patients.width, minWidth: columnWidths.patients.minWidth, textAlign: "center" }}>
																	{getPatientCount(nurseShift.id)}
																</StyledTableCell>
															)}

															{/* Total Workload - Hospital Demo Only */}
															{showHospitalView && (
																<StyledTableCell sx={{ width: columnWidths.workload.width, minWidth: columnWidths.workload.minWidth, textAlign: "center" }}>
																	{getTotalWorkload(getPatientCount(nurseShift.id), nurseShift.id)}
																</StyledTableCell>
															)}

															{/* Notes */}
															<StyledTableCell
																sx={{
																	width: columnWidths.notes.width,
																	minWidth: columnWidths.notes.minWidth,
																	textAlign: "left",
																	verticalAlign: "middle",
																}}
															>
																<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 1, width: "100%" }}>
																	{editingNotes?.id !== nurseShift.id ? (
																		<Box
																			sx={{
																				alignItems: "center",
																				gap: 1,
																				position: "relative",
																				textOverflow: "ellipsis",
																				display: "flex",
																				whiteSpace: "nowrap",
																				"&:hover .edit-icon": {
																					opacity: 1,
																				},
																			}}
																		>
																			<NurseShiftBadges
																				shift={nurseShift}
																				weekIndex={weekIndex}
																				isActual={showActual}
																				disableNotOnboardedBadge={showActual}
																				hasActiveReplaceRequest={!!badgeReplaceRequest}
																				activeReplaceRequest={badgeReplaceRequest}
																			/>
																			<Tooltip sx={{ display: "flex", "&:hover .edit-icon": { opacity: 1 } }} title={nurseShift.notes || ""}>
																				<span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "flex" }}>
																					{nurseShift.isPendingToUpdate && nurseShift.previousNotes ? (
																						<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
																							<Box sx={{ textDecoration: "line-through", color: "text.secondary" }}>{nurseShift.previousNotes}</Box>
																							<Box>{nurseShift.notes || ""}</Box>
																						</Box>
																					) : (
																						nurseShift.notes || ""
																					)}
																				</span>
																			</Tooltip>
																			<IconButton
																				data-testid="day-schedule-notes-edit-button"
																				className={editIconClassName}
																				size="small"
																				onClick={(e) => {
																					e.stopPropagation();
																					setEditingNotes({ id: nurseShift.id, element: e.currentTarget });
																				}}
																				sx={{
																					opacity: 0,
																					transition: "opacity 0.2s",
																					padding: "2px",
																					ml: "auto",
																					flexShrink: 0,
																					overflow: "auto",
																					color: "primary.dark",
																				}}
																			>
																				<EditIcon sx={{ fontSize: "1rem" }} />
																			</IconButton>
																		</Box>
																	) : (
																		<NurseInShiftNotesInput nurseInShift={nurseShift} onUpdateComplete={() => setEditingNotes(null)} />
																	)}
																</Box>
															</StyledTableCell>

															{/* Action */}
															<StyledTableCell
																sx={{
																	width: columnWidths.action.width,
																	minWidth: columnWidths.action.minWidth,
																}}
															>
																<Box sx={{ display: "flex", justifyContent: "end", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
																	{canViewWage &&
																		(!showActual || !!nurseShift.startTime) &&
																		!!nurseShift.cost &&
																		isWageBreakdownAvailable(nurseDetails, nurseShift.startTime, {
																			jobManagementEnabled,
																			shiftExcludeFromPpd: nurseShift.excludeFromPpd,
																		}) && (
																			<IconButton
																				size="small"
																				onClick={(e) => {
																					e.stopPropagation();
																					handleRowClick(nurseShift);
																				}}
																				sx={{ p: "2px", "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" } }}
																			>
																				{expandedRow === nurseShift.id ? (
																					<KeyboardArrowUp sx={{ fontSize: "20px" }} />
																				) : (
																					<KeyboardArrowDown sx={{ fontSize: "20px" }} />
																				)}
																			</IconButton>
																		)}
																	{canSendDirectMessage(nurseShift) && (
																		<IconButton
																			size="medium"
																			onClick={() => setDirectMessageNurseId(nurseShift.nurseId)}
																			sx={{ p: "2px", transition: "opacity 0.1s ease-in-out" }}
																		>
																			<MessageOutlined />
																		</IconButton>
																	)}
																	{!nurseShift.isPendingToDelete && (
																		<ShiftRemoveButton
																			onRemove={() => setDeleteNurse(nurseShift)}
																			isCallOffWalkthroughAnchor={nurseShift.id === callOffAnchorShiftId}
																		/>
																	)}
																	<IconButton
																		size="small"
																		onClick={(event) => {
																			handleActionsMenuOpen(event, nurseShift);
																		}}
																		sx={{ p: "2px", transition: "opacity 0.1s ease-in-out" }}
																	>
																		<MoreVert sx={{ fontSize: "20px" }} />
																	</IconButton>
																</Box>
															</StyledTableCell>
														</StyledTableRow>

														{/* Breakdown subtable — fetched on demand */}
														{nurseDetails && expandedRow === nurseShift.id && nurseShift.startTime && nurseShift.endTime && (
															<WageBreakdownSubTable
																columnCount={showHospitalView ? 8 : 6}
																breakdown={expandedBreakdown ?? undefined}
																isLoading={isBreakdownLoading}
																isError={isBreakdownError}
																onRetry={refetchBreakdown}
																nurse={{ id: nurseDetails.id, firstName: nurseDetails.firstName, name: nurseShift.name, role: nurseShift.role }}
																paidHours={shiftTotalHours}
																shiftId={nurseShift.id}
																autoScrollIntoView={reopened?.shiftId === nurseShift.id}
																opensWithDetails={reopened?.shiftId === nurseShift.id && reopened.detailsOpen}
															/>
														)}
													</AnalyticValuesProvider>
												);
											})}
										</TableBody>
									</Table>
								</Collapse>
							</TableCell>
						</TableRow>
					</Fragment>
				);
			})}
			<Dialog open={confirmationDialog.open} onClose={() => setConfirmationDialog({ open: false, title: "", message: "", onConfirm: () => {} })}>
				<DialogTitle>{confirmationDialog.title}</DialogTitle>
				<DialogContent>{confirmationDialog.message}</DialogContent>
				<DialogActions>
					<SystemButton buttonVariant="secondary" onClick={() => setConfirmationDialog({ open: false, title: "", message: "", onConfirm: () => {} })}>
						Cancel
					</SystemButton>
					<SystemButton onClick={confirmationDialog.onConfirm}>Confirm</SystemButton>
				</DialogActions>
			</Dialog>
			<Dialog open={ptoWarningState.open} onClose={() => setPtoWarningState({ open: false, onConfirm: () => {} })}>
				<DialogTitle>{t("pto.warning")}</DialogTitle>
				<DialogContent>{t("pto.warning.body")}</DialogContent>
				<DialogActions>
					<SystemButton buttonVariant="secondary" onClick={() => setPtoWarningState({ open: false, onConfirm: () => {} })}>
						{t("cancel")}
					</SystemButton>
					<SystemButton onClick={ptoWarningState.onConfirm}>{t("confirm")}</SystemButton>
				</DialogActions>
			</Dialog>
			{requestApprovalDialog.open && requestApprovalDialog.request && (
				<NormalizedRequestApprovalDialog
					open={requestApprovalDialog.open}
					onClose={() => setRequestApprovalDialog({ open: false, request: null, nurseShift: null })}
					request={requestApprovalDialog.request}
				/>
			)}
			{actionNurse && (
				<AnalyticValuesProvider
					props={{
						nurseInShiftId: actionNurse.id,
						recurringShiftId: actionNurse.recurringShiftId,
						nurseId: actionNurse.nurseId,
						dayNo: dayIndex,
						shiftNo: shiftTypeToIndex(actionNurse.shiftType),
						weekIndex,
					}}
				>
					<NormalizedNurseMenu
						key={`nurse-menu-${actionNurse.nurseId}`}
						nurseInShift={actionNurse}
						menuAnchorElement={actionsMenuAnchorEl}
						source={ChangesSource.DaySchedule}
						showActual={showActual}
						handleDeclineChanges={handleCancelClick}
						onClose={() => {
							setActionNurse(null);
							setActionsMenuAnchorEl(null);
						}}
					/>
				</AnalyticValuesProvider>
			)}
			{deleteNurse && (
				<DeleteRecurringOrSingleShift
					nurseInShift={deleteNurse}
					onFinished={() => setDeleteNurse(null)}
					onCancel={() => setDeleteNurse(null)}
					source={ChangesSource.DaySchedule}
					showActual={showActual}
				/>
			)}
		</>
	);
};
